import { Alert, Button, Input } from "antd";
import {
  AtOutline,
  CallOutline,
  KeyOutline,
  LockClosedOutline,
  MailOutline,
  ShieldCheckmarkOutline,
} from "@raresail/react-ionicons";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { alpha } from "@mui/material";
import { TAfterLogin } from "../../../@types/common";
import { useAuth } from "../../context/AuthContext";
import { useLocalStorage } from "../../../hooks/common/useLocalStorage";
import useAxios from "../../../hooks/common/useAxios";
import useSet from "../../../hooks/common/useSet";
import {
  API_OTP_REQUEST,
  API_OTP_VERIFY_LOGIN,
  API_PROFILE_ME,
  API_TOKEN,
} from "../../../constants/apis";
import {
  APP_NAME,
  ONBOARD,
  REGISTER,
  USER,
} from "../../../constants/constants";
import {
  PRIMARY_COLOR,
  PRIMARY_LIGHT,
  primary5,
  primary10,
} from "../../../constants/colors";
import { checkIsOnbarded } from "../../../utils/permissionUtils";

type Tab = "email" | "phone";

const TAB_META: Record<Tab, { label: string; icon: any; hint: string }> = {
  email: {
    label: "Email & Password",
    icon: AtOutline,
    hint: "Use your registered email and password",
  },
  phone: {
    label: "Phone & OTP",
    icon: CallOutline,
    hint: "We'll text you a 6-digit code",
  },
};

/**
 * Polished, unified login form.
 * - Segmented switcher (Email / Phone) with sliding pill background.
 * - Email tab: email + password.
 * - Phone tab: 2-step (phone → OTP code), with "use a different number" escape.
 * - Smooth fade between forms, no full-page reload.
 */
export const LoginForm = () => {
  const [tab, setTab] = useState<Tab>("email");

  // Email tab state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  // Phone tab state — step 1 collects phone, step 2 collects code
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [phoneStep, setPhoneStep] = useState<1 | 2>(1);

  const { uploadData, error, setError, submitting } = useSet();
  const { get } = useAxios();
  const { login } = useAuth();
  const { setData } = useLocalStorage();
  const navigate = useNavigate();

  // ── Shared after-login routing ─────────────────────────────────
  const afterLogin = ({ data, onSuccess }: TAfterLogin) => {
    get({
      api: API_PROFILE_ME,
      token: data?.access,
      successHandler: (me: any) => {
        setData(USER, me);
        onSuccess?.(me);
        navigate(checkIsOnbarded(me) ? "/" : `/${ONBOARD}`);
      },
    });
  };

  // ── Email submit ───────────────────────────────────────────────
  const onSubmitEmail = (e: any) => {
    e.preventDefault();
    setError(null);
    if (!email || !password) return;
    uploadData({
      api: API_TOKEN,
      params: { email, password },
      successMessage: false,
      onSuccess: (data: any) => {
        if (data?.access) login({ credentials: data, afterLogin });
      },
    });
  };

  // ── Phone: request OTP ─────────────────────────────────────────
  const onRequestOtp = (e: any) => {
    e.preventDefault();
    setError(null);
    if (!phone) return;
    uploadData({
      api: API_OTP_REQUEST,
      params: { phone_number: phone, purpose: "login" },
      successMessage: "If that phone number is registered, a code has been sent.",
      onSuccess: () => setPhoneStep(2),
    });
  };

  // ── Phone: verify OTP → JWT ────────────────────────────────────
  const onVerifyOtp = (e: any) => {
    e.preventDefault();
    setError(null);
    if (!code) return;
    uploadData({
      api: API_OTP_VERIFY_LOGIN,
      params: { phone_number: phone, code },
      successMessage: false,
      onSuccess: (data: any) => {
        if (data?.access) login({ credentials: data, afterLogin });
      },
    });
  };

  const switchTab = (next: Tab) => {
    if (next === tab) return;
    setError(null);
    setTab(next);
    if (next === "email") setPhoneStep(1);
  };

  const errorMessage = extractError(error);

  return (
    <Card>
      {/* Header */}
      <div className="flexColumn gap5">
        <div className="extraBold textDark" style={{ fontSize: 26, lineHeight: 1.15 }}>
          Welcome back
        </div>
        <div className="font13 grey">
          Sign in to continue to <b className="textDark">{APP_NAME}</b>.
        </div>
      </div>

      {/* Tab switcher with sliding pill */}
      <SwitcherWrapper>
        <SlidingPill $active={tab} />
        {(["email", "phone"] as Tab[]).map((t) => {
          const meta = TAB_META[t];
          const Icon = meta.icon;
          const active = tab === t;
          return (
            <SwitchButton
              key={t}
              type="button"
              onClick={() => switchTab(t)}
              $active={active}
            >
              <Icon style={{ fontSize: 18 }} />
              <span>{t === "email" ? "Email" : "Phone"}</span>
            </SwitchButton>
          );
        })}
      </SwitcherWrapper>

      <HintRow>
        <ShieldCheckmarkOutline style={{ fontSize: 14 }} />
        <span>{TAB_META[tab].hint}</span>
      </HintRow>

      {/* Form bodies */}
      {tab === "email" && (
        <form onSubmit={onSubmitEmail} className="flexColumn gap15">
          <Field label="Email Address">
            <Input
              size="large"
              type="email"
              autoComplete="email"
              prefix={<MailOutline style={{ fontSize: 16, color: "#94a3b8" }} />}
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoFocus
            />
          </Field>
          <Field
            label="Password"
            trailing={
              <Link
                to="/auth/reset-password"
                className="font12 main semiBold"
                style={{ textDecoration: "none" }}
              >
                Forgot?
              </Link>
            }
          >
            <Input.Password
              size="large"
              autoComplete="current-password"
              prefix={<LockClosedOutline style={{ fontSize: 16, color: "#94a3b8" }} />}
              placeholder="Your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </Field>

          {errorMessage && <Alert type="error" message={errorMessage} showIcon className="font12" />}

          <PrimaryButton
            type="primary"
            htmlType="submit"
            size="large"
            loading={submitting}
            block
            disabled={!email || !password}
          >
            Sign In
          </PrimaryButton>
        </form>
      )}

      {tab === "phone" && phoneStep === 1 && (
        <form onSubmit={onRequestOtp} className="flexColumn gap15">
          <Field label="Phone Number">
            <Input
              size="large"
              type="tel"
              autoComplete="tel"
              prefix={<CallOutline style={{ fontSize: 16, color: "#94a3b8" }} />}
              placeholder="+2567XXXXXXXX"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              autoFocus
            />
          </Field>

          {errorMessage && <Alert type="error" message={errorMessage} showIcon className="font12" />}

          <PrimaryButton
            type="primary"
            htmlType="submit"
            size="large"
            loading={submitting}
            block
            disabled={!phone}
          >
            Send Verification Code
          </PrimaryButton>
        </form>
      )}

      {tab === "phone" && phoneStep === 2 && (
        <form onSubmit={onVerifyOtp} className="flexColumn gap15">
          <PhonePill>
            <CallOutline style={{ fontSize: 14 }} />
            <span className="font12">{phone}</span>
            <button
              type="button"
              onClick={() => {
                setPhoneStep(1);
                setCode("");
                setError(null);
              }}
              className="font11 main semiBold"
              style={{
                background: "transparent",
                border: 0,
                cursor: "pointer",
                padding: 0,
              }}
            >
              Change
            </button>
          </PhonePill>

          <Field label="6-Digit Code">
            <Input
              size="large"
              inputMode="numeric"
              autoComplete="one-time-code"
              prefix={<KeyOutline style={{ fontSize: 16, color: "#94a3b8" }} />}
              placeholder="123456"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 8))}
              maxLength={8}
              autoFocus
            />
          </Field>

          {errorMessage && <Alert type="error" message={errorMessage} showIcon className="font12" />}

          <PrimaryButton
            type="primary"
            htmlType="submit"
            size="large"
            loading={submitting}
            block
            disabled={code.length < 4}
          >
            Verify & Sign In
          </PrimaryButton>

          <Button
            type="link"
            block
            onClick={onRequestOtp}
            disabled={submitting}
            style={{ marginTop: -6 }}
          >
            Didn't get a code? Resend
          </Button>
        </form>
      )}

      {/* Footer */}
      <FooterRow>
        <span className="font12 grey">Don't have an account?</span>
        <Link to={`/auth/${REGISTER}`} className="font13 main semiBold">
          Sign Up
        </Link>
      </FooterRow>
    </Card>
  );
};

const extractError = (error: any) => {
  if (!error) return null;
  if (typeof error === "string") return error;
  if (error?.message) return error.message;
  if (error?.detail) return error.detail;
  if (error?.non_field_errors)
    return Array.isArray(error.non_field_errors)
      ? error.non_field_errors[0]
      : error.non_field_errors;
  if (error?.code) return Array.isArray(error.code) ? error.code[0] : error.code;
  if (typeof error === "object") {
    const k = Object.keys(error).find((key) => key !== "status_code" && key !== "error_type");
    if (k) {
      const v = error[k];
      return Array.isArray(v) ? `${v[0]}` : `${v}`;
    }
  }
  return "Something went wrong. Please try again.";
};

/* ─── Small field wrapper ───────────────────────────────── */
const Field = ({
  label,
  trailing,
  children,
}: {
  label: string;
  trailing?: any;
  children: any;
}) => (
  <div className="flexColumn gap5">
    <div className="flexSpaceCenter">
      <div className="font12 semiBold textDark">{label}</div>
      {trailing}
    </div>
    {children}
  </div>
);

/* ─── Styles ──────────────────────────────────────────────── */

const Card = styled.div`
  background: #ffffff;
  border-radius: 16px;
  padding: 32px;
  width: 100%;
  max-width: 440px;
  display: flex;
  flex-direction: column;
  gap: 22px;
  color: #0f172a;
  box-shadow:
    0 1px 3px rgba(15, 23, 42, 0.04),
    0 12px 40px rgba(15, 23, 42, 0.08);

  @media (max-width: 600px) {
    padding: 24px 20px;
    border-radius: 14px;
  }
`;

const SwitcherWrapper = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: 1fr 1fr;
  background: ${PRIMARY_LIGHT};
  border-radius: 12px;
  padding: 4px;
  isolation: isolate;
`;

const SlidingPill = styled.div<{ $active: Tab }>`
  position: absolute;
  top: 4px;
  bottom: 4px;
  left: 4px;
  width: calc(50% - 4px);
  background: ${PRIMARY_COLOR};
  border-radius: 9px;
  z-index: -1;
  transform: translateX(${({ $active }) => ($active === "email" ? "0" : "100%")});
  transition: transform 0.28s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 1px 3px ${alpha(PRIMARY_COLOR, 0.25)};
`;

const SwitchButton = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 12px;
  background: transparent;
  border: 0;
  border-radius: 9px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  color: ${({ $active }) => ($active ? "#fff" : "#475569")};
  transition: color 0.2s ease;
  user-select: none;

  &:hover {
    color: ${({ $active }) => ($active ? "#fff" : PRIMARY_COLOR)};
  }
`;

const HintRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #64748b;
  font-size: 12px;
  background: ${primary5};
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid ${primary10};
`;

const PhonePill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: ${primary5};
  border: 1px solid ${primary10};
  color: #0f172a;
  padding: 8px 12px;
  border-radius: 999px;
  width: fit-content;
`;

const PrimaryButton = styled(Button)`
  height: 48px;
  font-weight: 600;
  letter-spacing: 0.2px;
  border-radius: 10px;
  &.ant-btn-primary,
  &.ant-btn-primary:hover,
  &.ant-btn-primary:focus,
  &.ant-btn-primary:active {
    background: ${PRIMARY_COLOR};
    color: #ffffff !important;
    border-color: ${PRIMARY_COLOR};
  }
  &.ant-btn-primary:hover {
    background: ${alpha(PRIMARY_COLOR, 0.92)} !important;
  }
  /* Make sure disabled state stays white-on-grey, not dark text on grey */
  &.ant-btn-primary[disabled],
  &.ant-btn-primary[disabled]:hover {
    color: #ffffff !important;
    background: ${alpha(PRIMARY_COLOR, 0.5)} !important;
    border-color: transparent;
  }
`;

const FooterRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding-top: 6px;
  border-top: 1px solid #f1f5f9;
  padding-top: 18px;
`;
